import { Button, CardActions, Stack } from "@mui/material";

import { CbhCustomChatMessage } from "@src/appV2/Chat/CbhCustomChatMessage";
import { ChatChannel } from "../chat.types";
import { CoreMessageType } from "@sendbird/uikit-react/types/utils";
import { getUserType } from "@src/appV2/PlacementCandidates/utils/helper";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { APP_V2_USER_EVENTS, logEvent, logError } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { addMinutes, format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import {
  canFetchPlacementApplications,
  transformResponseIntoPlacementApplication,
  useFetchPaginatedPlacementApplications,
} from "@src/appV2/PlacementApplications/api/useFetchPaginatedPlacementApplications";
import { isDefined } from "@clipboard-health/util-ts";
import { useCreateInterview } from "@src/appV2/Placements/api/useCreateInterview";
import { showErrorToast, showSuccessToast } from "@src/appV2/lib/Notifications";
import { API_TYPES } from "@src/appV2/api";
import { Text } from "@clipboard-health/ui-react";
import { useManagedWorkplaces } from "@src/appV2/PlacementCandidates/hooks/useManagedWorkplaces";
import { InterviewStatus } from "@src/appV2/Placements/types/fetchPlacements.schema";
import { InterviewType } from "@src/appV2/PlacementApplications/types/fetchInterviews.schema";
import { useGetPlacementsAccessLevel } from "@src/appV2/Placements/useGetPlacementsAccessLevel";

interface PlacementApplicationMessageProps {
  message: CoreMessageType;
  currentChannel: ChatChannel;
  channelUrl: string;
}

export function PlacementApplicationSuggestInterviewMessage({
  message,
  currentChannel,
  channelUrl,
}: PlacementApplicationMessageProps) {
  const { admin, profile, user } = useSession();
  const { isEnabled: isPlacementsEnabled } = useGetPlacementsAccessLevel();
  const { managingFacilityId } = currentChannel.metadata ?? {};
  const chatMessage = message as CoreMessageType & { message: string };

  const { hcpName, placementTitle, facilityName } = currentChannel.metadata ?? {};

  const { suggestedDateTime: suggestedDateTimeString } = JSON.parse(message.data) as {
    suggestedDateTime: string;
  };

  const suggestedDateTime = new Date(suggestedDateTimeString);
  const [workplaceId, workerId, placementId] = channelUrl.split("_") as Array<string | undefined>;
  const eventContext = {
    workplaceId,
    managingWorkplaceId: managingFacilityId,
    workerId,
    placementId,
    userId: user?._id,
    userType: getUserType(profile, admin),
    userEmail: user?.email,
    source: "chatBody",
    suggestedDateTime,
  };

  const { workplaces } = useManagedWorkplaces();

  const {
    data: placementApplicationsData,
    isLoading: isLoadingPlacementApplications,
    isError: isErrorPlacementApplications,
    refetch: refetchPlacementApplications,
  } = useFetchPaginatedPlacementApplications(
    {
      workplaceId: profile.userId,
      queryParams: {
        filter: {
          "placement.id": placementId,
          "worker.id": workerId,
          "workplace.id": workplaces.map((workplace) => workplace.id).join(","),
        },
      },
    },
    {
      enabled:
        isDefined(profile) &&
        isPlacementsEnabled &&
        !Number.isNaN(suggestedDateTime.getTime()) &&
        canFetchPlacementApplications(user?.access ?? []),
    }
  );

  const placementApplications = placementApplicationsData?.pages.flatMap(
    (placementApplicationsPage) =>
      placementApplicationsPage.data.map((placementApplication) =>
        transformResponseIntoPlacementApplication(
          placementApplication,
          placementApplicationsPage.included
        )
      )
  );
  const placementApplication = placementApplications?.at(0);

  const { mutateAsync: createInterview, isLoading: isCreating } = useCreateInterview({
    onSuccess: () => {
      showSuccessToast("Interview scheduled successfully");
      void refetchPlacementApplications();
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data.errors?.[0]?.detail ?? "Unable to schedule interview";
      showErrorToast(errorMessage);
    },
  });

  if (isLoadingPlacementApplications) {
    return null;
  }

  if (
    isErrorPlacementApplications ||
    Number.isNaN(suggestedDateTime.getTime()) ||
    !chatMessage.message ||
    !isDefined(placementApplication)
  ) {
    logError(APP_V2_APP_EVENTS.PLACEMENT_APPLICATION_SUGGEST_INTERVIEW_MESSAGE_RENDER_FAILURE, {
      error: new Error("Failed to render placement application suggest interview message"),
      metadata: {
        channelUrl,
        placementId,
        messageId: message.messageId,
        suggestedDateTime: suggestedDateTimeString,
      },
    });
    return null;
  }

  const hasInterviewBooked = placementApplication.interviews.some(
    (interview) =>
      interview.status === InterviewStatus.BOOKED &&
      interview.type === InterviewType.IN_PERSON_INTERVIEW
  );

  const formattedDate = format(
    utcToZonedTime(suggestedDateTime, profile?.tmz ?? "UTC"),
    "EEE, MMM d"
  );
  const formattedTime = format(utcToZonedTime(suggestedDateTime, profile?.tmz ?? "UTC"), "h:mm a");

  const messageToDisplay =
    !hcpName || !placementTitle || !facilityName || !suggestedDateTime
      ? chatMessage.message
      : `Great news! It looks like ${hcpName} has agreed to an interview on ${formattedDate} at ${formattedTime}.`;

  const handleBookInterview = async () => {
    logEvent(APP_V2_USER_EVENTS.PLACEMENT_CANDIDATE_BOOK_INTERVIEW_FROM_CHAT, eventContext);

    const defaultInterviewDurationInMinutes = 30;
    const dateEnd = addMinutes(suggestedDateTime, defaultInterviewDurationInMinutes);

    await createInterview({
      relatedResource: { type: API_TYPES.placementApplication, id: placementApplication.id },
      start: suggestedDateTime,
      end: dateEnd,
      bypassInterviewAvailabilityCheck: true,
      notifyInPlacementChatChannel: true,
    });
  };

  return (
    <CbhCustomChatMessage
      message={messageToDisplay}
      avatarSxProps={{ justifyContent: "flex-start" }}
      messageContainerSxProps={{ width: "50%" }}
    >
      <CardActions>
        <Stack sx={{ padding: 1 }} spacing={2} alignItems="flex-start">
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {hasInterviewBooked ? (
              <Text style={{ fontWeight: 700 }}>Interview scheduled</Text>
            ) : (
              <Button variant="contained" onClick={handleBookInterview} disabled={isCreating}>
                Book Interview
              </Button>
            )}
          </Stack>
        </Stack>
      </CardActions>
    </CbhCustomChatMessage>
  );
}
